import React, { useState, useEffect } from 'react';
import api from '../services/api';


import Swal from 'sweetalert2';

const SupportTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    subject: '',
    type: 'issue',
    description: '',
    priority: 'medium',
    attachment: null
  });
  const [replyMessage, setReplyMessage] = useState('');
  const [replyAttachment, setReplyAttachment] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      headers: { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    };
  };

  const loadTickets = async () => {
    setLoading(true);
    try {
      const response = await api.get('/tickets');
      setTickets(response.data);
    } catch (error) {
      console.error("Error loading tickets:", error);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to load tickets', timer: 1500, showConfirmButton: false });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTickets();
  }, []);

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    const data = new FormData();
    data.append('subject', formData.subject);
    data.append('type', formData.type);
    data.append('description', formData.description);
    data.append('priority', formData.priority);
    if (formData.attachment) {
      data.append('attachment', formData.attachment);
    }

    try {
      await api.post('/tickets', data, getAuthHeaders());
      Swal.fire({ icon: 'success', title: 'Success', text: 'Ticket created successfully', timer: 1500, showConfirmButton: false });
      setShowCreateModal(false);
      setFormData({ subject: '', type: 'issue', description: '', priority: 'medium', attachment: null });
      loadTickets();
    } catch (error) {
      console.error("Error creating ticket:", error);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to create ticket', timer: 1500, showConfirmButton: false });
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewTicket = async (id) => {
    try {
      const response = await api.get(`/tickets/${id}`);
      setSelectedTicket(response.data);
      setShowViewModal(true);
      setReplyMessage('');
      setReplyAttachment(null);
    } catch (error) {
      console.error("Error fetching ticket details:", error);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to load ticket details', timer: 1500, showConfirmButton: false });
    }
  };

  const handleReplySubmit = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim()) return;

    setSubmitting(true);
    const data = new FormData();
    data.append('message', replyMessage);
    if (replyAttachment) {
      data.append('attachment', replyAttachment);
    }

    try {
      const response = await api.post(`/tickets/${selectedTicket.id}/reply`, data, getAuthHeaders());
      Swal.fire({ icon: 'success', title: 'Success', text: 'Reply sent successfully', timer: 1500, showConfirmButton: false });
      setReplyMessage('');
      setReplyAttachment(null);
      // Update selected ticket state with new reply
      setSelectedTicket(prev => ({
        ...prev,
        replies: [...prev.replies, response.data.reply]
      }));
    } catch (error) {
      console.error("Error sending reply:", error);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to send reply', timer: 1500, showConfirmButton: false });
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      open: 'bg-primary',
      in_progress: 'bg-warning text-dark',
      resolved: 'bg-success',
      closed: 'bg-secondary'
    };
    return <span className={`badge ${badges[status]}`}>{status.replace('_', ' ').toUpperCase()}</span>;
  };

  const getPriorityBadge = (priority) => {
    const badges = { low: 'bg-info', medium: 'bg-warning text-dark', high: 'bg-danger' };
    return <span className={`badge ${badges[priority]}`}>{priority.toUpperCase()}</span>;
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold text-dark mb-1">Support Tickets</h2>
          <p className="text-muted mb-0">Need help? Create a ticket and the Super Admin will assist you.</p>
        </div>
        <button className="btn btn-primary rounded-pill px-4" onClick={() => setShowCreateModal(true)}>
          <i className="bi bi-plus-lg me-2"></i>Create Ticket
        </button>
      </div>

      <div className="card border-0 shadow-sm rounded-4">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="bg-light">
                <tr>
                  <th className="ps-4">Ticket ID</th>
                  <th>Subject</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Priority</th>
                  <th>Last Updated</th>
                  <th className="text-end pe-4">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="6" className="text-center py-5 text-muted">Loading tickets...</td></tr>
                ) : tickets.length > 0 ? (
                  tickets.map(ticket => (
                    <tr key={ticket.id}>
                      <td className="ps-4 fw-bold">#{ticket.id}</td>
                      <td>{ticket.subject}</td>
                      <td><span className="badge bg-secondary">{ticket.type?.toUpperCase()}</span></td>
                      <td>{getStatusBadge(ticket.status)}</td>
                      <td>{getPriorityBadge(ticket.priority)}</td>
                      <td className="text-muted small">{new Date(ticket.updated_at).toLocaleDateString()}</td>
                      <td className="text-end pe-4">
                        <button className="btn btn-sm btn-light text-primary rounded-pill px-3" onClick={() => handleViewTicket(ticket.id)}>
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-5">
                      <div className="text-muted">
                        <i className="bi bi-inbox fs-1 d-block mb-3"></i>
                        <p>No support tickets found.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create Ticket Modal */}
      {/* Create Ticket Modal */}
      {showCreateModal && <div className="modal-backdrop fade show"></div>}
      <div className={`modal fade ${showCreateModal ? 'show d-block' : ''}`} tabIndex="-1" style={showCreateModal ? {backgroundColor: 'rgba(0,0,0,0.5)'} : {}}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Create Support Ticket</h5>
              <button type="button" className="btn-close" onClick={() => setShowCreateModal(false)}></button>
            </div>
            <form onSubmit={handleCreateSubmit}>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label fw-bold">Subject</label>
                  <input type="text" className="form-control" value={formData.subject} onChange={e => setFormData({...formData, subject: e.target.value})} required />
                </div>
                <div className="mb-3">
                  <label className="form-label fw-bold">Ticket Type</label>
                  <select className="form-select" value={formData.type} onChange={e => setFormData({...formData, type: e.target.value})}>
                    <option value="issue">Issue/Bug</option>
                    <option value="suggestion">Suggestion</option>
                    <option value="update">Update Request</option>
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-bold">Priority</label>
                  <select className="form-select" value={formData.priority} onChange={e => setFormData({...formData, priority: e.target.value})}>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-bold">Description</label>
                  <textarea className="form-control" rows="5" value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} required placeholder="Please describe your issue or requirement in detail..."></textarea>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-bold">Attachment / Screenshot (Optional)</label>
                  <input type="file" className="form-control" accept="image/*,.pdf" onChange={e => setFormData({...formData, attachment: e.target.files[0]})} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={() => setShowCreateModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit Ticket'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>>

      {/* View Ticket Modal */}
      {/* View Ticket Modal */}
      {showViewModal && <div className="modal-backdrop fade show"></div>}
      <div className={`modal fade ${showViewModal ? 'show d-block' : ''}`} tabIndex="-1" style={showViewModal ? {backgroundColor: 'rgba(0,0,0,0.5)'} : {}}>
        <div className="modal-dialog modal-xl">
          <div className="modal-content">
            <div className="modal-header bg-light">
              <div className="d-flex align-items-center gap-2">
                <h5 className="modal-title mb-0">
                  <span className="fw-bold">Ticket #{selectedTicket?.id}</span>
                </h5>
                {selectedTicket && <span className="badge bg-secondary" style={{WebkitTextFillColor: 'initial'}}>{selectedTicket.type?.toUpperCase()}</span>}
                <div style={{WebkitTextFillColor: 'initial', display: 'flex', gap: '0.5rem'}}>
                  {selectedTicket && getStatusBadge(selectedTicket.status)}
                  {selectedTicket && getPriorityBadge(selectedTicket.priority)}
                </div>
              </div>
              <button type="button" className="btn-close" onClick={() => setShowViewModal(false)}></button>
            </div>
            <div className="modal-body bg-light px-4 py-4">
              {selectedTicket && (
                <div className="row g-4">
                  <div className="col-lg-8">
                    {/* Original Ticket */}
                    <div className="card border-0 shadow-sm mb-4">
                      <div className="card-header bg-white py-3">
                        <h5 className="mb-0 fw-bold">{selectedTicket.subject}</h5>
                        <small className="text-muted">Posted on {new Date(selectedTicket.created_at).toLocaleString()}</small>
                      </div>
                      <div className="card-body">
                        <p style={{ whiteSpace: 'pre-wrap' }}>{selectedTicket.description}</p>
                        {selectedTicket.attachment && (
                          <div className="mt-3">
                            <a href={`${process.env.NODE_ENV === "development" ? "http://localhost:8000" : "https://lms.appy.pk"}/${selectedTicket.attachment}`} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary">
                              <i className="bi bi-paperclip me-1"></i> View Attachment
                            </a>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Replies */}
                    <h6 className="fw-bold mb-3">Conversation History</h6>
                    {selectedTicket.replies?.map(reply => {
                      const isMine = reply.user_id === selectedTicket.user_id;
                      return (
                        <div key={reply.id} className={`d-flex mb-4 ${isMine ? 'justify-content-end' : ''}`}>
                          <div className={`card border-0 shadow-sm ${isMine ? 'bg-primary text-white' : 'bg-white'}`} style={{ maxWidth: '85%' }}>
                            <div className="card-body py-2 px-3">
                              <div className="d-flex justify-content-between mb-1" style={{ fontSize: '0.8rem' }}>
                                <span className={`fw-bold ${isMine ? 'text-light' : 'text-primary'}`}>{reply.user?.name}</span>
                                <span className={isMine ? 'text-light' : 'text-muted'}>{new Date(reply.created_at).toLocaleString()}</span>
                              </div>
                              <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{reply.message}</p>
                              {reply.attachment && (
                                <div className="mt-2">
                                  <a href={`${process.env.NODE_ENV === "development" ? "http://localhost:8000" : "https://lms.appy.pk"}/${reply.attachment}`} target="_blank" rel="noreferrer" className={`btn btn-sm ${isMine ? 'btn-light' : 'btn-outline-primary'}`}>
                                    View Attachment
                                  </a>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    })}

                    {/* Reply Box */}
                    {selectedTicket.status !== 'closed' && (
                      <div className="card border-0 shadow-sm mt-4">
                        <div className="card-body">
                          <form onSubmit={handleReplySubmit}>
                            <div className="mb-3">
                              <textarea className="form-control bg-light" rows="3" placeholder="Type your reply here..." value={replyMessage} onChange={e => setReplyMessage(e.target.value)} required></textarea>
                            </div>
                            <div className="d-flex justify-content-between align-items-center">
                              <input type="file" className="form-control form-control-sm w-auto" accept="image/*,.pdf" onChange={e => setReplyAttachment(e.target.files[0])} />
                              <button type="submit" className="btn btn-primary px-4" disabled={submitting}>
                                {submitting ? 'Sending...' : 'Send Reply'}
                              </button>
                            </div>
                          </form>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="col-lg-4">
                    <div className="card border-0 shadow-sm">
                      <div className="card-body">
                        <h6 className="fw-bold border-bottom pb-2 mb-3">Ticket Information</h6>
                        <div className="mb-2"><strong>Status:</strong> {getStatusBadge(selectedTicket.status)}</div>
                        <div className="mb-2"><strong>Priority:</strong> {getPriorityBadge(selectedTicket.priority)}</div>
                        <div className="mb-2"><strong>Created:</strong> {new Date(selectedTicket.created_at).toLocaleDateString()}</div>
                        <div className="mb-2"><strong>Last Update:</strong> {new Date(selectedTicket.updated_at).toLocaleString()}</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportTickets;
